// @givmo/app-admin — match-approval Idempotency-Key signature + wire fingerprint (07-1.2).
//
// The generic key-lifecycle holder now lives in `@givmo/payments` (R-20 hoist); this file keeps only
// what is admin-specific: the APPROVAL SIGNATURE that defines series identity, the bounded WIRE
// fingerprint that keeps grouped-approve keys under the backend's 255-char cap, and a factory that
// binds the holder to the signature.
//
// The dashboard Approve / Approve-all charges a card via `POST /admin/company/matches/{id}/approve`
// (+ the grouped twin), which the backend `claim_idempotency` dependency de-dupes ONLY across retries
// carrying the SAME `Idempotency-Key`. An UNKEYED retry mints a fresh server-side uuid4 and does NOT
// cross-retry-dedup — so a stable client key is the ONLY thing standing between a network retry /
// double-click and a DOUBLE CHARGE.
//
// Series identity for an approval = the exact set of match ids being approved PLUS the funding source
// (a specific saved card, or the company wallet) PLUS the cover-fees choice (CAM-BE13: covering is a
// different gross). A different funding source, a different set of matches, or the other choice is a
// genuinely different charge → a fresh key.

import { IdempotencyKeyHolder } from '@givmo/payments';

/**
 * The funding source for an approval charge — a specific saved card, or the company wallet. It is
 * part of the series identity: switching between a card and the wallet (or between two cards) is a
 * different charge instrument and MUST re-scope the idempotency series.
 */
export type ApprovalFunding =
  | { readonly kind: 'card'; readonly cardId: string }
  | { readonly kind: 'wallet' };

/**
 * A stable signature of the approval being charged; a change ends the current attempt-series. The
 * discriminator is the exact set of match ids being approved PLUS the funding source — a different
 * card, the wallet instead of a card, or a different set of matches, is a genuinely different charge
 * and MUST get a fresh key.
 */
export interface ApprovalSignature {
  /** The match ids in this charge, order-independent. */
  readonly matchIds: readonly string[];
  /** The funding source (a funding change re-scopes the charge → a new series). */
  readonly funding: ApprovalFunding;
  /**
   * The approver's "Cover processing fees" tick (CAM-BE13). Covering charges a different gross, so
   * it is a different series; absent or `false` is the unticked approval.
   */
  readonly coverFees?: boolean;
}

/**
 * Order-independent fingerprint of an approval: the funding source + sorted match ids, then `|cover`
 * when the fees are covered. The uncovered CARD form is byte-identical to the pre-wallet fingerprint
 * (`card:<id>|matches:<ids>`), so an uncovered card series' key derivation is unchanged; the wallet
 * form (`wallet|matches:<ids>`) is distinct by construction, so a wallet approval can never collide
 * with (or replay) a card approval of the same match set, and a covered approval never collides with
 * an uncovered one.
 */
export function approvalFingerprint(signature: ApprovalSignature): string {
  const ids = [...signature.matchIds].sort().join(',');
  const funding =
    signature.funding.kind === 'wallet' ? 'wallet' : `card:${signature.funding.cardId}`;
  return `${funding}|matches:${ids}${signature.coverFees === true ? '|cover' : ''}`;
}

/**
 * Bounded WIRE form of the approval fingerprint: a fixed-length 32-hex hash of
 * {@link approvalFingerprint}. The raw fingerprint comma-joins every match
 * UUID, so its length GROWS WITH LEG COUNT — a 6-leg ETF group produced a
 * 308-char Idempotency-Key, over the backend's 255-char cap
 * (`MAX_IDEMPOTENCY_KEY_LENGTH`), 400-ing every grouped approve with >=5 legs
 * (07-CUT0 re-test finding (4)). The hash keeps the property that matters —
 * deterministic per (sorted match set, funding source), so retries of the SAME
 * action reuse the SAME wire key — at a length that is constant for any leg count
 * (`<uuid>:<32 hex>` = 69 chars).
 *
 * DELIBERATELY SYNCHRONOUS (FNV-1a 64-bit, two seed variants — belt and
 * suspenders, NOT independent lanes: FNV's structure correlates same-message
 * lane collisions, so honest capacity is ~64 bits, which is still orders of
 * magnitude beyond this threat model; do not expand this suffix's role to a
 * global key without the uuid seed): the charge
 * loop must not gain a suspension point between the budget pre-check and the
 * charge POST, and this suffix is not a security boundary — global uniqueness
 * comes from the per-series UUID seed; the suffix only separates the handful
 * of (funding-source, match-set) variants within one dialog series, and the backend
 * additionally fingerprints the request body server-side.
 */
export function approvalWireFingerprint(signature: ApprovalSignature): string {
  const raw = approvalFingerprint(signature);
  return fnv1a64Hex(raw, 0xcbf29ce484222325n) + fnv1a64Hex(raw, 0x84222325cbf29ce4n);
}

/** FNV-1a 64-bit over UTF-16 code units, hex-padded to 16 chars. Pure + fast. */
function fnv1a64Hex(input: string, seed: bigint): string {
  const PRIME = 0x100000001b3n;
  const MASK = 0xffffffffffffffffn;
  let hash = seed;
  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * PRIME) & MASK;
  }
  return hash.toString(16).padStart(16, '0');
}

/**
 * Construct the match-approval idempotency-key holder, bound to the admin {@link ApprovalSignature}
 * via {@link approvalFingerprint}. A React component holds ONE holder across its whole lifetime (a
 * `useRef`), so the key is stable across re-renders by construction. `generate` is injectable for
 * deterministic tests.
 */
export function createIdempotencyKeyHolder(
  generate?: () => string,
): IdempotencyKeyHolder<ApprovalSignature> {
  return new IdempotencyKeyHolder<ApprovalSignature>(approvalFingerprint, generate);
}
